/**
 * Resolve a schedule slot's display name.
 *
 * Priority chain, per Phase 8 timeline-redesign spec:
 *   contentMap[slot.contentId]?.title
 *   contentMap[slot.contentId]?.name
 *   contentMap[slot.contentId]?.filename
 *   "(untitled)"
 *
 * We must never render a raw id. If a candidate "looks like an id" (Phase 8
 * Bug 1 leak: legacy DB rows where contentId is the filename/hash itself),
 * we treat it as missing and fall through.
 */

// Only flag pure alphanumeric blobs 12+ chars (nanoid/hash shape). Names with
// underscores or hyphens — Tower_Back, Welcome_Video — are human-curated and
// must pass through even though legacy rows sometimes put them in contentId.
const LOOKS_LIKE_ID_RE = /^[A-Za-z0-9]{12,}$/;

export const looksLikeRawId = (x) => {
  if (!x) return false;
  const s = String(x);
  if (!LOOKS_LIKE_ID_RE.test(s)) return false;
  if (/\s/.test(s)) return false;
  if (/\.[a-z]{2,4}$/i.test(s)) return false;
  return true;
};

const pickHuman = (candidate) => {
  if (candidate == null) return null;
  const s = String(candidate).trim();
  if (!s) return null;
  if (looksLikeRawId(s)) return null;
  return s;
};

/**
 * Resolve a slot to a human label using the content map.
 * Returns { label, resolved } — resolved=false when we had to fall back to
 * "(untitled)" (no contentMap hit and no clean denormalised title on the slot).
 */
export const resolveSlotLabel = (slot, contentMap) => {
  if (!slot) return { label: '(untitled)', resolved: false };
  const byId = slot.contentId ? contentMap?.get?.(slot.contentId) : null;

  // Prefer the content doc's human fields, then fall back to the slot's own
  // denormalised `name`. The slot.name fallback matters when the referenced
  // content has been trashed/archived — contentMap excludes those items —
  // but the backend projection still ships a readable name like
  // "Key_New_1080X1920_copy_3" on the slot itself.
  const chain = [byId?.title, byId?.name, byId?.filename, slot.name];

  for (const c of chain) {
    const picked = pickHuman(c);
    if (picked) return { label: picked, resolved: true };
  }
  if (slot.contentId) {
    return { label: String(slot.contentId), resolved: false };
  }
  return { label: '(untitled)', resolved: false };
};

/**
 * Resolve thumbnail + media type for a slot. Returns null when unknown.
 */
export const resolveSlotAsset = (slot, contentMap) => {
  if (!slot) return null;
  const byId = slot.contentId ? contentMap?.get?.(slot.contentId) : null;
  if (!byId) return null;
  return {
    thumbnailUrl: byId.thumbnailUrl || null,
    mediaType: byId.mediaType || byId.mimeType || null,
  };
};
